import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Link as LinkIcon, Calendar, MessageSquare, ShieldCheck, BadgeCheck, Share2, Link2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { userService } from '../../../services/userService';
import { UserProfile } from '../../../types/api';
import defaultCover from '../../../assets/images/clouds2.png';
import CoverImageUpload from '../../../components/Profile/shared/CoverImageUpload';
import { FollowButton } from '../../../components/UI/FollowButton';
import { FollowListModal } from '../../../components/Profile/shared/FollowListModal';
import ImagePreviewModal from '../../../components/UI/ImagePreviewModal';
import { useToast } from '../../../components/UI/Toast';
import { useFollowData, useUserStats } from '../../../hooks/useProfile';
import { buildProfilePath, buildProfileShareText, buildProfileShareUrl, buildShareClipboardText, copyTextToClipboard } from '../../../utils/share';
import { DEFAULT_AVATAR_URL, resolveAvatarUrl } from '../../../constants/assets';

interface ProfileHeaderProps {
    isOwnProfile?: boolean;
    userId?: string;
    profile?: UserProfile | null;
    loading?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ isOwnProfile = true, userId, profile: profileProp, loading: loadingProp }) => {
    const [profile, setProfile] = useState<UserProfile | null>(profileProp || null);
    const [loading, setLoading] = useState(loadingProp ?? true);
    const navigate = useNavigate();
    const [coverUrl, setCoverUrl] = useState<string>('');
    const [hasAvatarError, setHasAvatarError] = useState(false);
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [showAvatarPreview, setShowAvatarPreview] = useState(false);
    const [isShareCopied, setIsShareCopied] = useState(false);
    const shareResetTimerRef = useRef<number | null>(null);
    const { showToast } = useToast();

    // Follow data via SWR
    const resolvedUserId = profileProp?.userId || profile?.userId || userId;
    const { followersCount, followingCount, refresh: refreshFollows } = useFollowData(resolvedUserId);
    const { totalLikes, averageRating } = useUserStats(resolvedUserId);

    const renderOfficialBadge = (compact = false) => (
        <span
            className={compact
                ? 'inline-flex h-6 min-w-9 items-center justify-center rounded-full border-[3px] border-white bg-amber-500 px-1.5 text-[10px] font-bold leading-none text-white shadow-sm'
                : 'inline-flex h-6 items-center rounded-md border border-amber-200 bg-amber-50 px-2 text-xs font-bold leading-none text-amber-700'
            }
            title="官方用户"
        >
            官方
        </span>
    );

    // Follow list Modals
    const [showFollowersModal, setShowFollowersModal] = useState(false);
    const [showFollowingModal, setShowFollowingModal] = useState(false);

    // Sync profile from parent prop
    useEffect(() => {
        if (profileProp) {
            setProfile(profileProp);
            setCoverUrl(profileProp.coverImageUrl || '');
            if (loadingProp === undefined) {
                setLoading(false);
            }
        }
    }, [profileProp, loadingProp]);

    // Sync loading from parent prop
    useEffect(() => {
        if (loadingProp !== undefined) {
            setLoading(loadingProp);
        }
    }, [loadingProp]);

    useEffect(() => {
        setHasAvatarError(false);
    }, [profileProp?.avatarUrl, profile?.avatarUrl]);

    useEffect(() => {
        return () => {
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        let isActive = true;

        const fetchProfile = async () => {
            let actualUserId = userId;
            try {
                let response;
                if (isOwnProfile || !userId) {
                    response = await userService.getUserProfile();
                } else {
                    response = await userService.getUserProfileById(userId);
                }

                if (response.success && response.data?.userProfile && isActive) {
                    setProfile(response.data.userProfile);
                    setCoverUrl(response.data.userProfile.coverImageUrl || '');
                    actualUserId = response.data.userProfile.userId;
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
            } finally {
                if (isActive && loadingProp === undefined) {
                    setLoading(false);
                }
            }

            return actualUserId;
        };

        const loadData = async () => {
            await fetchProfile();
        };

        loadData();

        const handleFollowUpdate = () => {
            refreshFollows();
        };

        window.addEventListener('follow_updated', handleFollowUpdate);

        return () => {
            isActive = false;
            window.removeEventListener('follow_updated', handleFollowUpdate);
        };
    }, [isOwnProfile, userId, profileProp?.userId, profile?.userId, loadingProp, refreshFollows]);

    const handleCoverUpload = async (base64DataUrl: string): Promise<boolean> => {
        setIsUploadingCover(true);
        try {
            const result = await userService.updateCoverImage(base64DataUrl);
            if (result.success) {
                setCoverUrl(base64DataUrl);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Cover upload error:', error);
            return false;
        } finally {
            setIsUploadingCover(false);
        }
    };

    if (loading) {
        return <div className="h-64 w-full flex items-center justify-center animate-pulse bg-gray-50 rounded-[2.5rem]">加载中...</div>;
    }

    // Default empty profile structure to prevent UI from disappearing on error
    const defaultProfile = {
        userId: 'default_user',
        role: 'user',
        displayName: '',
        username: 'default_user',
        email: '',
        phone: '',
        wechat: '',
        avatarUrl: '', // Will use fallback in img src
        coverImageUrl: '', // Will use fallback in img src
        bio: '',
        location: '',
        website: '',
        verified: false,
        enterpriseVerified: false,
        accountType: 'individual',
        creatorInfo: { tags: [], skills: [], portfolioHighlight: [] },
        settings: { showContact: false, allowDirectOrder: false, allowDirectMessage: false },
        createdAt: Date.now(),
        updatedAt: Date.now()
    } as UserProfile;

    const displayProfile = profile ? { ...profile } : defaultProfile;
    const enterpriseVerified = Boolean(displayProfile.enterpriseVerified);
    const resolvedAvatarUrl = hasAvatarError
        ? DEFAULT_AVATAR_URL
        : resolveAvatarUrl(displayProfile.avatarUrl);
    const profileDisplayName = displayProfile.displayName || displayProfile.username || displayProfile.userId || '这位创作者';
    const profileShareUrl = buildProfileShareUrl(displayProfile.userId);
    const profileShareText = buildProfileShareText(profileDisplayName);

    const handleShare = async () => {
        try {
            await copyTextToClipboard(buildShareClipboardText(profileShareText, profileShareUrl));
            setIsShareCopied(true);
            if (shareResetTimerRef.current) {
                window.clearTimeout(shareResetTimerRef.current);
            }
            shareResetTimerRef.current = window.setTimeout(() => {
                setIsShareCopied(false);
            }, 1800);
            showToast('分享信息已复制', 'success');
        } catch (error) {
            console.error('Copy profile share content failed:', error);
            showToast('分享信息复制失败', 'error');
        }
    };

    return (
        <div className="relative mb-8">
            {/* Cover Image */}
            <div className="h-64 w-full rounded-[2.5rem] overflow-hidden relative group">
                <img
                    src={coverUrl || displayProfile.coverImageUrl || defaultCover}
                    alt="封面"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                {/* Cover Edit Button - only show for own profile */}
                {isOwnProfile && (
                    <CoverImageUpload
                        currentCoverUrl={coverUrl || displayProfile.coverImageUrl || defaultCover}
                        onUpload={handleCoverUpload}
                        isUploading={isUploadingCover}
                    />
                )}
            </div>

            <div className="px-6 md:px-10">
                <div className="flex flex-col md:flex-row gap-6 md:items-start relative -mt-20">

                    {/* Avatar Column */}
                    <div className="flex flex-col items-center md:items-start shrink-0 gap-4">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowAvatarPreview(true)}
                                className="group relative block rounded-full"
                                aria-label="查看头像大图"
                            >
                                <div className="w-40 h-40 rounded-full p-1.5 bg-white shadow-xl transition-transform group-hover:scale-[1.02]">
                                    <img
                                        src={resolvedAvatarUrl}
                                        alt="Profile"
                                        className="w-full h-full rounded-full object-cover border border-gray-100"
                                        onError={() => setHasAvatarError(true)}
                                    />
                                </div>
                                <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/38 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                                    查看大图
                                </span>
                            </button>
                            {/* Online Status / Badge */}
                            {displayProfile.isOfficial ? (
                                <div className="absolute bottom-4 right-2">
                                    {renderOfficialBadge(true)}
                                </div>
                            ) : enterpriseVerified ? (
                                <div className="absolute bottom-4 right-4 w-6 h-6 bg-yellow-500 rounded-full border-4 border-white flex items-center justify-center shadow-sm" title="企业认证">
                                    <ShieldCheck className="w-3.5 h-3.5 text-white" />
                                </div>
                            ) : displayProfile.verified && (
                                <div className="absolute bottom-4 right-4 w-6 h-6 bg-blue-500 rounded-full border-4 border-white flex items-center justify-center shadow-sm" title="实名认证">
                                    <BadgeCheck className="w-3.5 h-3.5 text-white" />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-6 text-gray-500 pl-2 md:pl-0 mt-3 font-medium text-[13px]">
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-bold text-gray-900 text-base">{totalLikes >= 1000 ? `${(totalLikes / 1000).toFixed(1)}k` : totalLikes}</span> 获赞
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-bold text-gray-900 text-base">{averageRating || '-'}</span> 评分
                            </div>
                        </div>
                    </div>


                    {/* Info Section */}
                    <div className="flex-1 pt-4 md:pt-24 space-y-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                                    {displayProfile.displayName || displayProfile.username || displayProfile.userId || "User"}
                                    {displayProfile.isOfficial ? (
                                        renderOfficialBadge()
                                    ) : enterpriseVerified ? (
                                        <ShieldCheck className="w-5 h-5 text-yellow-500 fill-yellow-50" />
                                    ) : displayProfile.verified && (
                                        <BadgeCheck className="w-5 h-5 text-blue-500 fill-blue-50" />
                                    )}
                                </h1>
                                <p className="text-gray-500 text-sm mt-1 font-mono">id: {displayProfile.userId}</p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3">
                                {isOwnProfile ? (
                                    <>
                                        <Link to="/profile/edit" className="w-28 h-10 rounded-full bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm flex items-center justify-center text-sm">
                                            编辑资料
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => void handleShare()}
                                            className="h-10 w-10 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm flex items-center justify-center"
                                            title="分享主页"
                                        >
                                            {isShareCopied ? <Link2 className="w-4 h-4 text-blue-600" /> : <Share2 className="w-4 h-4" />}
                                        </button>
                                    </>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <FollowButton userId={displayProfile.userId} className="h-10 px-6 font-medium text-sm" />
                                        <button
                                            onClick={() => {
                                                const convId = `C2C${displayProfile.userId}`;
                                                navigate(`/messages/${convId}`, {
                                                    state: {
                                                        returnTo: buildProfilePath(displayProfile.userId)
                                                    }
                                                });
                                            }}
                                            className="h-10 min-w-[80px] px-4 rounded-full bg-black text-white font-medium text-sm hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
                                        >
                                            <MessageSquare className="w-4 h-4" />
                                            聊一聊
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => void handleShare()}
                                            className="h-10 w-10 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm flex items-center justify-center"
                                            title="分享主页"
                                        >
                                            {isShareCopied ? <Link2 className="w-4 h-4 text-blue-600" /> : <Share2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bio & Details */}
                        <p className="text-gray-600 max-w-2xl leading-relaxed">
                            {displayProfile.bio || "暂无简介"}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 pt-1">
                            {displayProfile.creatorInfo?.tags?.length ? (
                                displayProfile.creatorInfo.tags.map(tag => (
                                    <span key={tag} className="px-3 py-1 bg-gray-100/80 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors cursor-default">
                                        {tag}
                                    </span>
                                ))
                            ) : (
                                <span className="text-sm text-gray-400">暂无标签</span>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                            {displayProfile.location && (
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    {displayProfile.location}
                                </div>
                            )}
                            {displayProfile.website && (() => {
                                const raw = displayProfile.website.trim();
                                // Users often enter "example.com" without a protocol; without
                                // one, the browser treats it as a relative path and rewrites
                                // to https://craftcloud.top/example.com on click.
                                const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                                const display = raw.replace(/^https?:\/\//i, '');
                                return (
                                    <div className="flex items-center gap-2 hover:text-blue-600 cursor-pointer transition-colors">
                                        <LinkIcon className="w-4 h-4" />
                                        <a href={href} target="_blank" rel="noopener noreferrer">{display}</a>
                                    </div>
                                );
                            })()}
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {new Date(displayProfile.createdAt).getFullYear()}年加入
                            </div>
                        </div>

                    </div>

                    {/* Stats Section */}
                    <div className="flex flex-row md:flex-col gap-8 md:gap-5 pt-4 md:pt-24 border-t md:border-t-0 md:border-l border-gray-100 md:pl-10 md:min-w-[120px]">
                        <div className="flex flex-col items-start cursor-pointer hover:opacity-75 transition-opacity" onClick={() => setShowFollowersModal(true)}>
                            <div className="text-2xl font-bold text-gray-900 leading-none">{followersCount}</div>
                            <div className="text-[13px] text-gray-500 mt-1.5 font-medium">粉丝</div>
                        </div>
                        <div className="flex flex-col items-start cursor-pointer hover:opacity-75 transition-opacity" onClick={() => setShowFollowingModal(true)}>
                            <div className="text-2xl font-bold text-gray-900 leading-none">{followingCount}</div>
                            <div className="text-[13px] text-gray-500 mt-1.5 font-medium">关注</div>
                        </div>
                    </div>
                </div>
            </div>

            <FollowListModal
                isOpen={showFollowersModal}
                onClose={() => setShowFollowersModal(false)}
                type="followers"
                userId={displayProfile.userId}
            />
            <FollowListModal
                isOpen={showFollowingModal}
                onClose={() => setShowFollowingModal(false)}
                type="following"
                userId={displayProfile.userId}
            />

            <ImagePreviewModal
                isOpen={showAvatarPreview}
                imageUrl={resolvedAvatarUrl}
                alt={`${displayProfile.displayName || displayProfile.username || displayProfile.userId} 的头像`}
                title="头像预览"
                onClose={() => setShowAvatarPreview(false)}
            />
        </div>
    );
};

export default ProfileHeader;
